import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function AIContentPage() {
  useSEO({
    title: 'AI 콘텐츠 제작 - KR STUDIO | 인공지능 기반 콘텐츠 솔루션',
    description: 'KR스튜디오의 AI 콘텐츠 제작 서비스로 효율적이고 혁신적인 교육 콘텐츠를 만드세요. AI 기술을 활용한 차세대 이러닝 솔루션입니다.',
    keywords: 'AI 콘텐츠, 인공지능 콘텐츠, AI 교육, 자동화 제작, 스마트 콘텐츠',
    canonical: '/ai-content',
    schema: createServiceSchema(
      'AI 콘텐츠 제작 서비스',
      'AI 기술을 활용한 효율적이고 혁신적인 차세대 교육 콘텐츠 제작'
    ),
  });

  const { t } = useTranslation();
  const [monthlyVideos, setMonthlyVideos] = useState(10);
  const [costPerVideo, setCostPerVideo] = useState(500000);
  const [productionDays, setProductionDays] = useState(7);

  const aiTech = [
    {
      icon: 'ri-mic-line',
      title: '음성 합성',
      features: [
        { label: 'ElevenLabs 한국어 최적화', highlight: false },
        { label: '40종 음색 선택', highlight: false },
        { label: '감정 표현 가능', highlight: true }
      ],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-video-line',
      title: '영상 생성',
      features: [
        { label: 'AI 아나운서 (D-ID)', highlight: false },
        { label: '립싱크 완벽 구현', highlight: true },
        { label: '제스처 자연스러움', highlight: false }
      ],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-closed-captioning-line',
      title: '자막 처리',
      features: [
        { label: 'Whisper 기반 STT', highlight: false },
        { label: '98% 정확도', highlight: true },
        { label: '실시간 다국어 번역', highlight: false }
      ],
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const scenarios = [
    {
      icon: 'ri-megaphone-line',
      title: '공지사항 자동화',
      before: '매번 촬영 필요',
      after: '텍스트만 입력하면 즉시 생성',
      color: 'blue'
    },
    {
      icon: 'ri-book-open-line',
      title: '교육 콘텐츠 대량 생산',
      before: '강사 섭외 어려움',
      after: 'AI 아나운서로 무제한 제작',
      color: 'purple'
    },
    {
      icon: 'ri-global-line',
      title: '글로벌 콘텐츠',
      before: '각국 번역 및 더빙',
      after: '원클릭 다국어 변환',
      color: 'green'
    }
  ];

  const processSteps = [
    { step: '1', title: '원고 작성', icon: 'ri-file-text-line', color: 'blue' },
    { step: '2', title: 'AI 음성 생성', icon: 'ri-mic-line', color: 'purple' },
    { step: '3', title: '영상 매칭', icon: 'ri-video-add-line', color: 'pink' },
    { step: '4', title: '자막 삽입', icon: 'ri-closed-captioning-line', color: 'orange' },
    { step: '5', title: '품질 검수', icon: 'ri-checkbox-circle-line', color: 'green' },
    { step: '6', title: '즉시 납품', icon: 'ri-rocket-line', color: 'cyan' }
  ];

  const payAsYouGo = [
    { service: 'AI 음성', price: '1분당 5천원', icon: 'ri-mic-line' },
    { service: 'AI 아나운서', price: '1분당 2만원', icon: 'ri-user-voice-line' },
    { service: '자막 생성', price: '1분당 3천원', icon: 'ri-closed-captioning-line' }
  ];

  const subscriptionPlans = [
    { videos: '50편', price: '100만원', popular: false },
    { videos: '100편', price: '180만원', popular: true },
    { videos: '무제한', price: '300만원', popular: false }
  ];

  // ROI Calculations
  const currentMonthlyCost = monthlyVideos * costPerVideo;
  const aiMonthlyCost = currentMonthlyCost * 0.3; // 70% savings
  const costSavings = currentMonthlyCost - aiMonthlyCost;
  const timeSavings = productionDays * 0.85; // 85% time savings
  const roiMonths = Math.ceil((300000 / costSavings) || 2); // Assuming 300k setup cost

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/87de5e4ecf6a1d2f6bd653c5cdbe5f0c.jpg"
            alt="AI 콘텐츠 제작"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-pink-900/85"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mr-4">
                <i className="ri-robot-2-line text-3xl text-white"></i>
              </div>
              <div className="h-1 w-20 bg-gradient-to-r from-cyan-400 to-transparent"></div>
            </div>
            <h1 className="text-6xl md:text-7xl font-black text-white leading-tight mb-6">
              AI가 바꾸는<br />
              콘텐츠 제작의 미래
            </h1>
            <p className="text-3xl text-cyan-300 mb-8 font-bold">
              사람 없이도, 품질은 그대로
            </p>
            <div className="hidden flex gap-4">
              <a href="#demo" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-4 text-lg font-bold hover:shadow-2xl hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap">
                AI 데모 체험
              </a>
              <a href="#trial" className="border-2 border-white text-white px-10 py-4 text-lg font-bold hover:bg-white hover:text-purple-900 transition cursor-pointer whitespace-nowrap">
                무료 AI 콘텐츠 체험하기
              </a>
            </div>
          </div>
        </div>

        {/* Floating Stats */}
        <div className="absolute bottom-8 left-0 right-0">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-3 gap-4 max-w-3xl">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 text-center">
                <div className="text-3xl font-black text-cyan-400">70%</div>
                <div className="text-sm text-white/80">비용 절감</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 text-center">
                <div className="text-3xl font-black text-purple-400">85%</div>
                <div className="text-sm text-white/80">시간 단축</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 text-center">
                <div className="text-3xl font-black text-pink-400">30분</div>
                <div className="text-sm text-white/80">제작 시간</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Tech Stack Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 font-bold mb-4">
              🤖 우리가 사용하는 AI
            </div>
            <h2 className="text-5xl font-black mb-4">AI 기술 스택</h2>
            <p className="text-xl text-gray-600">최첨단 AI 기술로 완성하는 콘텐츠</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {aiTech.map((tech, index) => (
              <div key={index} className="bg-white p-8 shadow-xl hover:shadow-2xl transition-all group cursor-pointer border-t-4 border-transparent hover:border-current" style={{ borderTopColor: `var(--tw-gradient-from)` }}>
                <div className={`w-20 h-20 bg-gradient-to-br ${tech.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-all`}>
                  <i className={`${tech.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-black mb-6">{tech.title}</h3>
                <ul className="space-y-3">
                  {tech.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className={`ri-checkbox-circle-fill ${feature.highlight ? 'text-cyan-500' : 'text-gray-400'} mr-3 mt-1`}></i>
                      <span className={feature.highlight ? 'font-bold text-gray-900' : 'text-gray-600'}>
                        {feature.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scenarios Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">활용 시나리오</h2>
            <p className="text-xl text-gray-600">AI로 해결하는 콘텐츠 제작 고민</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {scenarios.map((scenario, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 hover:shadow-xl transition-all cursor-pointer border border-gray-100">
                <div className={`w-16 h-16 bg-${scenario.color}-100 flex items-center justify-center mb-6`}>
                  <i className={`${scenario.icon} text-3xl text-${scenario.color}-600`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-6">{scenario.title}</h3>
                
                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4">
                    <div className="text-xs text-red-600 font-bold mb-1">BEFORE</div>
                    <div className="text-sm text-gray-700">{scenario.before}</div>
                  </div>
                  
                  <div className="flex justify-center">
                    <i className="ri-arrow-down-line text-2xl text-gray-400"></i>
                  </div>
                  
                  <div className="bg-green-50 border-l-4 border-green-500 p-4">
                    <div className="text-xs text-green-600 font-bold mb-1">AFTER</div>
                    <div className="text-sm font-bold text-gray-900">{scenario.after}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="demo" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">AI 콘텐츠 프로세스</h2>
            <p className="text-xl text-gray-300">6단계로 완성되는 AI 콘텐츠</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-6 gap-4 mb-12">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className={`w-20 h-20 bg-gradient-to-br from-${step.color}-500 to-${step.color}-600 flex items-center justify-center mx-auto mb-4 relative`}>
                    <i className={`${step.icon} text-3xl text-white`}></i>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white text-gray-900 flex items-center justify-center font-black text-sm">
                      {step.step}
                    </div>
                  </div>
                  <div className="text-sm font-bold">{step.title}</div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gradient-to-r from-gray-600 to-gray-700" style={{ transform: 'translateX(50%)' }}></div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center bg-gradient-to-r from-cyan-500 to-blue-500 p-8 mt-8">
              <div className="flex items-center justify-center gap-4">
                <i className="ri-time-line text-4xl"></i>
                <div>
                  <div className="text-sm text-cyan-100">전체 소요 시간</div>
                  <div className="text-5xl font-black">30분</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">가격 정책</h2>
            <p className="text-xl text-gray-600">필요한 만큼만, 합리적으로</p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            {/* Pay As You Go */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 border-2 border-gray-200">
              <div className="flex items-center mb-6">
                <i className="ri-money-dollar-circle-line text-4xl text-blue-600 mr-4"></i>
                <h3 className="text-3xl font-black">사용량 기반</h3>
              </div>
              <p className="text-gray-600 mb-8">필요한 만큼만 사용하세요</p>
              
              <div className="space-y-4">
                {payAsYouGo.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white border border-gray-200">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mr-4">
                        <i className={`${item.icon} text-xl text-blue-600`}></i>
                      </div>
                      <span className="font-bold text-gray-900">{item.service}</span>
                    </div>
                    <span className="text-blue-600 font-black text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Subscription */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 border-2 border-purple-300">
              <div className="flex items-center mb-6">
                <i className="ri-vip-crown-line text-4xl text-purple-600 mr-4"></i>
                <h3 className="text-3xl font-black">구독형</h3>
              </div>
              <p className="text-gray-600 mb-8">대량 제작에 최적화</p>
              
              <div className="space-y-4">
                {subscriptionPlans.map((plan, index) => (
                  <div key={index} className={`p-6 ${plan.popular ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-white border border-purple-200'} relative`}>
                    {plan.popular && (
                      <div className="absolute -top-3 right-4 bg-yellow-400 text-gray-900 px-4 py-1 text-xs font-black">
                        POPULAR
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-sm ${plan.popular ? 'text-purple-200' : 'text-gray-600'} mb-1`}>월 제작량</div>
                        <div className="text-3xl font-black">{plan.videos}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm ${plan.popular ? 'text-purple-200' : 'text-gray-600'} mb-1`}>월 비용</div>
                        <div className="text-3xl font-black">{plan.price}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 font-bold mb-4">
              💰 ROI 계산기
            </div>
            <h2 className="text-5xl font-black mb-4">얼마나 절감되나요?</h2>
            <p className="text-xl text-gray-600">현재 제작 비용을 입력해보세요</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="bg-white p-8 shadow-xl border-2 border-gray-200">
                <h3 className="text-2xl font-black mb-6 text-gray-900">현재 콘텐츠 제작 비용</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      월 제작 편수
                    </label>
                    <input 
                      type="number" 
                      value={monthlyVideos}
                      onChange={(e) => setMonthlyVideos(Number(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-gray-300 focus:border-blue-500 outline-none text-lg font-bold"
                      min="1"
                    />
                    <div className="text-sm text-gray-500 mt-1">{monthlyVideos}편</div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      편당 비용 (원)
                    </label>
                    <input 
                      type="number" 
                      value={costPerVideo}
                      onChange={(e) => setCostPerVideo(Number(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-gray-300 focus:border-blue-500 outline-none text-lg font-bold"
                      min="0"
                      step="10000"
                    />
                    <div className="text-sm text-gray-500 mt-1">{costPerVideo.toLocaleString()}원</div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      제작 기간 (일)
                    </label>
                    <input 
                      type="number" 
                      value={productionDays}
                      onChange={(e) => setProductionDays(Number(e.target.value))}
                      className="w-full px-4 py-3 border-2 border-gray-300 focus:border-blue-500 outline-none text-lg font-bold"
                      min="1"
                    />
                    <div className="text-sm text-gray-500 mt-1">{productionDays}일</div>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 text-white">
                <h3 className="text-2xl font-black mb-6">AI 도입 시 예상 절감</h3>
                
                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-100">현재 월 비용</span>
                      <span className="text-2xl font-black">{currentMonthlyCost.toLocaleString()}원</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-100">AI 도입 후</span>
                      <span className="text-2xl font-black">{aiMonthlyCost.toLocaleString()}원</span>
                    </div>
                    <div className="h-px bg-white/30 my-4"></div>
                    <div className="flex items-center justify-between">
                      <span className="font-bold">비용 절감</span>
                      <div className="text-right">
                        <div className="text-3xl font-black text-yellow-300">{costSavings.toLocaleString()}원</div>
                        <div className="text-sm text-green-200">70% 절감</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <span className="font-bold">시간 절감</span>
                      <div className="text-right">
                        <div className="text-3xl font-black text-yellow-300">{timeSavings.toFixed(1)}일</div>
                        <div className="text-sm text-green-200">85% 단축</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
                    <div className="flex items-center justify-between">
                      <span className="font-bold">ROI 달성</span>
                      <div className="text-right">
                        <div className="text-3xl font-black text-yellow-300">{roiMonths}개월</div>
                        <div className="text-sm text-green-200">투자 회수 기간</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Application Section */}
      <section id="trial" className="hidden py-20 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-6 py-2 font-black mb-4">
                🎁 무료 파일럿 제작
              </div>
              <h2 className="text-5xl font-black mb-4">지금 신청하기</h2>
              <p className="text-xl text-purple-200">1편 무료 제작 • 원고만 제공하시면 됩니다 • 24시간 내 결과물 전달</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-8 md:p-12">
              <form className="space-y-6" data-readdy-form id="ai-trial-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2">회사명 *</label>
                    <input 
                      type="text" 
                      name="company"
                      required
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none"
                      placeholder="회사명을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">담당자명 *</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none"
                      placeholder="이름을 입력하세요"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold mb-2">이메일 *</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2">연락처 *</label>
                    <input 
                      type="tel" 
                      name="phone"
                      required
                      className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none"
                      placeholder="010-0000-0000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">콘텐츠 유형 *</label>
                  <select 
                    name="content_type"
                    required
                    className="w-full px-4 py-3 pr-8 bg-white/20 border border-white/30 text-white focus:border-cyan-400 outline-none cursor-pointer"
                  >
                    <option value="" className="text-gray-900">선택하세요</option>
                    <option value="announcement" className="text-gray-900">공지사항</option>
                    <option value="education" className="text-gray-900">교육 콘텐츠</option>
                    <option value="marketing" className="text-gray-900">마케팅 영상</option>
                    <option value="news" className="text-gray-900">뉴스/브리핑</option>
                    <option value="other" className="text-gray-900">기타</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">원고 내용 *</label>
                  <textarea 
                    name="script"
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none resize-none"
                    placeholder="AI로 제작할 원고를 입력하세요 (최대 500자)"
                  ></textarea>
                  <div className="text-sm text-purple-200 mt-1">최대 500자까지 입력 가능합니다</div>
                </div>

                <div>
                  <label className="block text-sm font-bold mb-2">추가 요청사항</label>
                  <textarea 
                    name="additional_requests"
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/20 border border-white/30 text-white placeholder-white/50 focus:border-cyan-400 outline-none resize-none"
                    placeholder="음색, 스타일 등 추가 요청사항을 입력하세요 (최대 500자)"
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 text-lg font-black hover:shadow-2xl hover:shadow-cyan-500/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  무료 체험 신청하기
                </button>

                <p className="text-sm text-center text-purple-200">
                  신청 후 24시간 내에 AI로 제작된 샘플 영상을 받아보실 수 있습니다
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
